import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useMemo } from "react";
import {
  SparkleIcon, GrowthIcon, ChartIcon, ShieldIcon,
  ClockIcon, BookIcon, FinanceIcon, WalletIcon,
  ReceiptIcon, CheckIcon, ArrowRightIcon, DeviceIcon,
  DocumentIcon, TransportIcon, MealIcon,
} from "@/components/ui/TriIcons";

const EASE = [0.4, 0, 0.2, 1] as const;
const WINE = "#7B1416";
const WINE_L = "#A42022";
const SAND = "#C4A882";
const CHARCOAL = "#1C1917";
const CREAM = "#F5EFE4";
const CREAM2 = "#EDE4D4";
const FONT_DISPLAY = "'Georgia', 'Times New Roman', 'Playfair Display', serif";
const FONT_SANS = "-apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', system-ui, sans-serif";

// ============================================================
// AI INTELLIGENCE LAYER — completely redesigned
// ============================================================
const aiModels = [
  { id: "revenue", label: "Revenue Forecasting", icon: GrowthIcon, color: WINE, accuracy: 94, detail: "Predict next term's collection rate within 3% margin", dataset: "1.2M transactions", features: ["Payment history", "Term cycles", "Economic indicators", "Family profiles"] },
  { id: "dropout", label: "Dropout Risk", icon: ShieldIcon, color: CHARCOAL, accuracy: 89, detail: "Identify at-risk students weeks before they withdraw", dataset: "847K enrollment records", features: ["Attendance trend", "Grade trajectory", "Fee payment", "Engagement signals"] },
  { id: "collection", label: "Fee Collection", icon: FinanceIcon, color: WINE, accuracy: 91, detail: "Forecast which families will pay late or default", dataset: "2.1M payment events", features: ["Payment history", "Income proxies", "Reminder response", "Wallet balance"] },
  { id: "attendance", label: "Attendance", icon: ClockIcon, color: CHARCOAL, accuracy: 96, detail: "Predict absenteeism patterns before they escalate", dataset: "5.4M check-ins", features: ["Day-of-week", "Weather", "Transport status", "Health visits"] },
  { id: "academic", label: "Academic Performance", icon: BookIcon, color: WINE, accuracy: 87, detail: "Flag students likely to underperform this term", dataset: "1.8M grade entries", features: ["Assignment scores", "Class participation", "Study time", "Prior performance"] },
  { id: "transport", label: "Transport Delays", icon: TransportIcon, color: CHARCOAL, accuracy: 93, detail: "Predict route delays from traffic and weather data", dataset: "920K GPS pings/day", features: ["Traffic patterns", "Weather", "Route history", "Driver behavior"] },
  { id: "inventory", label: "Inventory Demand", icon: WalletIcon, color: WINE, accuracy: 90, detail: "Forecast bookstore and cafeteria demand per week", dataset: "340K purchase records", features: ["Seasonal trends", "Enrollment count", "Past sales", "Event calendar"] },
  { id: "welfare", label: "Student Welfare", icon: SparkleIcon, color: CHARCOAL, accuracy: 85, detail: "Detect welfare concerns from behavioral signals", dataset: "156K welfare signals", features: ["Mood indicators", "Social patterns", "Academic changes", "Health visits"] },
];

const networkLayers = [
  { count: 5, x: 50 },
  { count: 7, x: 200 },
  { count: 7, x: 350 },
  { count: 4, x: 500 },
];

const liveFeedLines = [
  "→ Model trained on 1.2M interactions",
  "→ Processing 8,420 events/sec",
  "→ 3 risk alerts generated today",
  "→ Forecast updated 4 minutes ago",
  "→ 12 interventions recommended",
  "→ Accuracy improved 0.3% this week",
];

export function AISection() {
  const [activeIdx, setActiveIdx] = useState(0);
  const [feedIdx, setFeedIdx] = useState(0);
  const [pulse, setPulse] = useState(0);

  const active = aiModels[activeIdx];

  useEffect(() => {
    const id = setInterval(() => setActiveIdx((p) => (p + 1) % aiModels.length), 4000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const id = setInterval(() => setFeedIdx((p) => (p + 1) % liveFeedLines.length), 1800);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const id = setInterval(() => setPulse((p) => p + 1), 1200);
    return () => clearInterval(id);
  }, []);

  const nodes = useMemo(() => {
    const result: { x: number; y: number; layer: number; idx: number }[] = [];
    networkLayers.forEach((layer, li) => {
      const spacing = 300 / (layer.count + 1);
      for (let i = 0; i < layer.count; i++) {
        result.push({ x: layer.x, y: 50 + spacing * (i + 1), layer: li, idx: i });
      }
    });
    return result;
  }, []);

  const connections = useMemo(() => {
    const conns: { from: { x: number; y: number }; to: { x: number; y: number }; key: string }[] = [];
    for (let li = 0; li < networkLayers.length - 1; li++) {
      const layerNodes = nodes.filter(n => n.layer === li);
      const nextNodes = nodes.filter(n => n.layer === li + 1);
      layerNodes.forEach(from => {
        nextNodes.forEach(to => {
          conns.push({ from: { x: from.x, y: from.y }, to: { x: to.x, y: to.y }, key: `${from.layer}-${from.idx}-${to.idx}` });
        });
      });
    }
    return conns;
  }, [nodes]);

  return (
    <div className="grid items-center gap-12 lg:grid-cols-[1fr_1.15fr] lg:gap-16">
      {/* LEFT — Copy + model selector */}
      <div>
        <div className="mb-6 flex items-center gap-4">
          <div className="h-px w-12" style={{ background: WINE }} />
          <span className="text-[11px] font-medium uppercase tracking-[0.28em]" style={{ color: WINE }}>
            Intelligence Layer
          </span>
        </div>
        <h2 className="font-display text-4xl font-medium leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-[3.5rem] text-balance">
          Every interaction becomes <em className="italic" style={{ color: WINE }}>intelligence</em>.
        </h2>
        <p className="mt-6 text-lg leading-[1.7] text-white/80">
          Every payment. Every journey. Every lesson. Every click. TriSphere AI
          continuously learns from every interaction across the platform — predicting
          outcomes before they happen, and recommending actions before they're needed.
        </p>

        {/* Stats row */}
        <div className="mt-8 flex gap-6">
          {[
            { value: "12+", label: "Prediction models" },
            { value: "8.4K", label: "Events / second" },
            { value: "1.2M", label: "Training data points" },
          ].map(s => (
            <div key={s.label}>
              <p className="font-display text-2xl font-medium" style={{ color: WINE }}>{s.value}</p>
              <p className="text-[10px] uppercase tracking-wider text-white/60">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Model selector grid */}
        <div className="mt-8 grid grid-cols-2 gap-2.5">
          {aiModels.map((m, i) => {
            const isActive = i === activeIdx;
            return (
              <motion.button
                key={m.id}
                onClick={() => setActiveIdx(i)}
                onMouseEnter={() => setActiveIdx(i)}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04, duration: 0.4 }}
                className="group flex items-center gap-3 rounded-xl border p-3 text-left transition-all duration-300"
                style={{
                  borderColor: isActive ? `${m.color}50` : "rgba(255,255,255,0.06)",
                  background: isActive ? `${m.color}0C` : "rgba(255,255,255,0.02)",
                }}
              >
                <div
                  className="grid h-9 w-9 shrink-0 place-items-center rounded-lg transition-all"
                  style={{ background: isActive ? `${m.color}25` : `${m.color}12` }}
                >
                  <m.icon className="h-4 w-4" accent={m.color} style={{ color: m.color }} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-semibold text-white/95">{m.label}</p>
                  <div className="mt-1 flex items-center gap-1.5">
                    <div className="h-1 w-12 overflow-hidden rounded-full bg-white/[0.08]">
                      <motion.div
                        className="h-full rounded-full"
                        style={{ background: m.color }}
                        animate={{ width: isActive ? `${m.accuracy}%` : `${m.accuracy * 0.6}%` }}
                        transition={{ duration: 0.4 }}
                      />
                    </div>
                    <span className="text-[10px] tabular-nums" style={{ color: m.color }}>{m.accuracy}%</span>
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* RIGHT — AI prediction visualization — completely redesigned */}
      <div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="overflow-hidden rounded-3xl border bg-white/[0.03] backdrop-blur-xl"
          style={{ borderColor: "rgba(255,255,255,0.06)" }}
        >
          {/* Top bar — model header */}
          <div className="relative flex items-center justify-between border-b border-white/[0.06] px-6 py-5">
            <div className="flex items-center gap-3">
              <motion.div
                animate={{ scale: [1, 1.08, 1] }}
                transition={{ duration: 3, repeat: Infinity }}
                className="grid h-12 w-12 place-items-center rounded-2xl"
                style={{ background: `${active.color}18` }}
              >
                <active.icon className="h-6 w-6" accent={active.color} style={{ color: active.color }} />
              </motion.div>
              <div>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeIdx}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.3 }}
                  >
                    <p className="font-display text-lg font-medium text-white">{active.label}</p>
                    <p className="text-xs text-white/70">{active.detail}</p>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
            <div className="text-right">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeIdx}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.3 }}
                  className="font-display text-3xl font-medium tabular-nums"
                  style={{ color: active.color }}
                >
                  {active.accuracy}%
                </motion.div>
              </AnimatePresence>
              <p className="text-[9px] uppercase tracking-wider text-white/55">Accuracy</p>
            </div>
          </div>

          {/* Neural network visualization */}
          <div className="relative px-6 py-8">
            <p className="mb-4 text-[10px] font-medium uppercase tracking-wider text-white/55">Neural Network · Live Inference</p>
            <div className="relative" style={{ height: 320 }}>
              <svg className="absolute inset-0 h-full w-full" viewBox="0 0 550 320" preserveAspectRatio="xMidYMid meet">
                {/* Connections */}
                {connections.map((conn, i) => (
                  <motion.line
                    key={conn.key}
                    x1={conn.from.x} y1={conn.from.y}
                    x2={conn.to.x} y2={conn.to.y}
                    stroke={active.color}
                    strokeWidth={0.5}
                    strokeOpacity={0.08}
                    animate={{ strokeOpacity: [0.04, 0.12, 0.04] }}
                    transition={{ duration: 2, delay: (i % 10) * 0.1, repeat: Infinity }}
                  />
                ))}
                {/* Animated signal pulses along connections */}
                {connections.filter((_, i) => i % 7 === pulse % 7).map((conn, i) => (
                  <motion.circle
                    key={`pulse-${conn.key}-${i}`}
                    r={2}
                    fill={active.color}
                    initial={{ cx: conn.from.x, cy: conn.from.y, opacity: 0 }}
                    animate={{
                      cx: [conn.from.x, conn.to.x],
                      cy: [conn.from.y, conn.to.y],
                      opacity: [0, 0.8, 0],
                    }}
                    transition={{ duration: 1.2, ease: "easeInOut" }}
                  />
                ))}
                {/* Nodes */}
                {nodes.map((node, i) => (
                  <g key={`node-${i}`}>
                    <motion.circle
                      cx={node.x}
                      cy={node.y}
                      r={6}
                      fill={node.layer === 0 ? CHARCOAL : node.layer === networkLayers.length - 1 ? WINE : active.color}
                      fillOpacity={0.2}
                      stroke={node.layer === 0 ? CHARCOAL : node.layer === networkLayers.length - 1 ? WINE : active.color}
                      strokeWidth={1}
                      strokeOpacity={0.4}
                      animate={{
                        r: [5, 7, 5],
                        fillOpacity: [0.15, 0.3, 0.15],
                      }}
                      transition={{
                        duration: 2 + (i % 3),
                        delay: (i % 5) * 0.2,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                    />
                    <motion.circle
                      cx={node.x}
                      cy={node.y}
                      r={2}
                      fill={node.layer === 0 ? CHARCOAL : node.layer === networkLayers.length - 1 ? WINE : active.color}
                      fillOpacity={0.6}
                      animate={{ fillOpacity: [0.4, 0.8, 0.4] }}
                      transition={{ duration: 1.5, delay: (i % 4) * 0.15, repeat: Infinity }}
                    />
                  </g>
                ))}
                {/* Layer labels */}
                <text x={50} y={305} fill="rgba(255,255,255,0.5)" fontSize="8" textAnchor="middle" fontFamily="monospace">INPUT</text>
                <text x={275} y={305} fill="rgba(255,255,255,0.5)" fontSize="8" textAnchor="middle" fontFamily="monospace">HIDDEN LAYERS</text>
                <text x={500} y={305} fill="rgba(255,255,255,0.5)" fontSize="8" textAnchor="middle" fontFamily="monospace">OUTPUT</text>
              </svg>
            </div>
          </div>

          {/* Feature importance + confidence */}
          <div className="grid grid-cols-2 gap-px bg-white/[0.04]">
            {/* Feature importance */}
            <div className="bg-white/[0.02] p-5">
              <p className="mb-3 text-[10px] font-medium uppercase tracking-wider text-white/55">Top predictive features</p>
              <div className="space-y-2.5">
                {active.features.map((f, i) => {
                  const val = 0.82 - i * 0.15;
                  return (
                    <motion.div
                      key={f}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.06 }}
                      className="flex items-center gap-2"
                    >
                      <span className="w-20 text-[10px] text-white/70">{f}</span>
                      <div className="h-1 flex-1 overflow-hidden rounded-full bg-white/[0.06]">
                        <motion.div
                          className="h-full rounded-full"
                          style={{ background: active.color }}
                          animate={{ width: `${val * 100}%` }}
                          transition={{ delay: i * 0.06 + 0.1, duration: 0.5 }}
                        />
                      </div>
                      <span className="w-8 text-right text-[10px] tabular-nums text-white/60">{(val * 100).toFixed(0)}%</span>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Confidence gauge */}
            <div className="bg-white/[0.02] p-5">
              <p className="mb-3 text-[10px] font-medium uppercase tracking-wider text-white/55">Model confidence</p>
              <div className="flex flex-col items-center justify-center gap-3 pt-2">
                {/* Circular progress ring */}
                <div className="relative h-24 w-24">
                  <svg className="h-full w-full -rotate-90" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="6" />
                    <motion.circle
                      cx="50" cy="50" r="42" fill="none" stroke={active.color} strokeWidth="6"
                      strokeLinecap="round"
                      strokeDasharray={2 * Math.PI * 42}
                      animate={{ strokeDashoffset: 2 * Math.PI * 42 * (1 - active.accuracy / 100) }}
                      transition={{ duration: 0.8, ease: EASE }}
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="font-display text-2xl font-medium tabular-nums" style={{ color: active.color }}>{active.accuracy}%</span>
                    <span className="text-[8px] uppercase tracking-wider text-white/55">confidence</span>
                  </div>
                </div>
                <p className="text-[10px] text-white/60 text-center">{active.dataset}</p>
              </div>
            </div>
          </div>

          {/* Live prediction stream */}
          <div className="border-t border-white/[0.06] bg-black/20 p-5">
            <div className="flex items-center gap-2 mb-3">
              <motion.div
                animate={{ opacity: [1, 0.3, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="h-2 w-2 rounded-full"
                style={{ background: active.color }}
              />
              <span className="text-[10px] font-medium uppercase tracking-wider text-white/65">Live prediction stream</span>
            </div>
            <div className="h-5 overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.div
                  key={feedIdx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  className="font-mono text-[11px] text-white/65"
                >
                  {liveFeedLines[feedIdx]}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

// ============================================================
// ANALYTICS DASHBOARDS
// ============================================================
export function AnalyticsSection() {
  const [activeTab, setActiveTab] = useState(0);
  const tabs = ["Revenue", "Attendance", "Collections", "Transport"];

  return (
    <div className="grid items-center gap-12 lg:grid-cols-[1fr_1.4fr] lg:gap-16">
      {/* LEFT — Copy */}
      <div>
        <div className="mb-6 flex items-center gap-4">
          <div className="h-px w-12" style={{ background: CHARCOAL }} />
          <span className="text-[11px] font-medium uppercase tracking-[0.28em]" style={{ color: CHARCOAL }}>
            Analytics
          </span>
        </div>
        <h2 className="font-display text-4xl font-medium leading-[1.1] tracking-tight sm:text-5xl lg:text-[3.5rem] text-balance" style={{ color: CHARCOAL }}>
          Dashboards that make institutions <em className="italic" style={{ color: WINE }}>omniscient</em>.
        </h2>
        <p className="mt-6 text-lg leading-[1.7] text-[#4A4A4A]">
          Revenue, attendance, collections, transport, commerce, learning, engagement —
          every metric, live. No spreadsheets. No quarterly reports. Just the truth,
          in real time.
        </p>

        <div className="mt-8 space-y-3">
          {[
            "Real-time data — updated every 3 seconds",
            "Custom report builder — no SQL required",
            "Board-ready exports — PDF, Excel, CSV",
            "Predictive forecasting — next term, next year",
            "Heat maps & cohort comparisons",
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.4 }}
              className="flex items-center gap-3"
            >
              <div className="grid h-6 w-6 shrink-0 place-items-center rounded-full" style={{ background: `${CHARCOAL}15` }}>
                <CheckIcon className="h-3.5 w-3.5" accent={CHARCOAL} style={{ color: CHARCOAL }} />
              </div>
              <span className="text-sm text-[#4A4A4A]">{item}</span>
            </motion.div>
          ))}
        </div>
      </div>

      {/* RIGHT — Dashboard mockup */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="overflow-hidden rounded-2xl border bg-white shadow-float"
        style={{ borderColor: "rgba(26,26,26,0.06)" }}
      >
        {/* Dashboard header */}
        <div className="flex items-center justify-between border-b border-black/[0.04] px-6 py-4">
          <div>
            <p className="font-display text-lg font-medium" style={{ color: CHARCOAL }}>Command Center</p>
            <p className="text-xs text-[#999]">Kampala International School · Live</p>
          </div>
          <div className="flex items-center gap-2">
            <motion.div
              animate={{ opacity: [1, 0.3, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="h-2 w-2 rounded-full bg-green-400"
            />
            <span className="text-xs text-[#999]">Real-time</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-black/[0.04] px-6">
          {tabs.map((t, i) => (
            <button
              key={t}
              onClick={() => setActiveTab(i)}
              className="relative py-3 text-sm transition"
              style={{ color: activeTab === i ? CHARCOAL : "#999", fontWeight: activeTab === i ? 600 : 400 }}
            >
              {t}
              {activeTab === i && (
                <motion.div
                  layoutId="tab-underline"
                  className="absolute bottom-0 left-0 right-0 h-0.5"
                  style={{ background: CHARCOAL }}
                />
              )}
            </button>
          ))}
        </div>

        {/* Dashboard content */}
        <div className="p-6">
          {/* KPI cards */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: "Revenue", value: "UGX 284M", change: "+24%", color: CHARCOAL },
              { label: "Collection rate", value: "94.2%", change: "+3.1%", color: WINE },
              { label: "Students", value: "1,842", change: "+18", color: CHARCOAL },
            ].map((kpi) => (
              <div key={kpi.label} className="rounded-xl border border-black/[0.04] p-4" style={{ background: CREAM }}>
                <p className="text-[10px] uppercase tracking-wider text-[#999]">{kpi.label}</p>
                <p className="mt-1 font-display text-xl font-medium" style={{ color: CHARCOAL }}>{kpi.value}</p>
                <p className="mt-1 text-xs font-medium" style={{ color: kpi.color }}>{kpi.change}</p>
              </div>
            ))}
          </div>

          {/* Chart */}
          <div className="mt-4 rounded-xl border border-black/[0.04] p-5" style={{ background: CREAM }}>
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium" style={{ color: CHARCOAL }}>{tabs[activeTab]} trend — last 12 weeks</p>
              <div className="flex items-center gap-3 text-[10px]">
                <span className="flex items-center gap-1.5">
                  <div className="h-2 w-2 rounded-full" style={{ background: CHARCOAL }} />
                  <span className="text-[#999]">This year</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <div className="h-2 w-2 rounded-full" style={{ background: WINE }} />
                  <span className="text-[#999]">Last year</span>
                </span>
              </div>
            </div>
            {/* Animated bar chart */}
            <div className="mt-4 flex h-32 items-end gap-1.5">
              {Array.from({ length: 12 }).map((_, i) => {
                const h1 = 40 + Math.sin(i * 0.7) * 20 + (activeTab === 0 ? i * 3 : 0);
                const h2 = 30 + Math.sin(i * 0.5) * 15;
                return (
                  <div key={i} className="flex flex-1 flex-col items-center gap-1">
                    <div className="flex w-full items-end justify-center gap-0.5">
                      <motion.div
                        initial={{ height: 0 }}
                        whileInView={{ height: `${Math.max(h1, 10)}%` }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.05, type: "spring", stiffness: 80 }}
                        className="w-1/2 rounded-t"
                        style={{ background: CHARCOAL, opacity: 0.8 }}
                      />
                      <motion.div
                        initial={{ height: 0 }}
                        whileInView={{ height: `${Math.max(h2, 8)}%` }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.05 + 0.1, type: "spring", stiffness: 80 }}
                        className="w-1/2 rounded-t"
                        style={{ background: WINE, opacity: 0.5 }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-2 flex justify-between text-[9px] text-[#bbb]">
              {["W1", "W2", "W3", "W4", "W5", "W6", "W7", "W8", "W9", "W10", "W11", "W12"].map(w => (
                <span key={w}>{w}</span>
              ))}
            </div>
          </div>

          {/* Heat map row */}
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-black/[0.04] p-4" style={{ background: CREAM }}>
              <p className="text-[10px] uppercase tracking-wider text-[#999]">Attendance heat map</p>
              <div className="mt-3 grid grid-cols-7 gap-1">
                {Array.from({ length: 35 }).map((_, i) => {
                  const intensity = Math.random();
                  return (
                    <div
                      key={i}
                      className="aspect-square rounded-sm"
                      style={{
                        background: `rgba(11,61,53,${intensity * 0.8 + 0.1})`,
                      }}
                    />
                  );
                })}
              </div>
            </div>
            <div className="rounded-xl border border-black/[0.04] p-4" style={{ background: CREAM }}>
              <p className="text-[10px] uppercase tracking-wider text-[#999]">Collection funnel</p>
              <div className="mt-3 space-y-2">
                {[
                  { label: "Invoiced", value: 100, color: CHARCOAL },
                  { label: "Paid", value: 94, color: CHARCOAL },
                  { label: "Reconciled", value: 91, color: WINE },
                ].map((s) => (
                  <div key={s.label}>
                    <div className="flex items-center justify-between text-[10px]">
                      <span className="text-[#999]">{s.label}</span>
                      <span className="font-medium" style={{ color: CHARCOAL }}>{s.value}%</span>
                    </div>
                    <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-black/[0.04]">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${s.value}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="h-full rounded-full"
                        style={{ background: s.color }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ============================================================
// INTEGRATIONS ECOSYSTEM
// ============================================================
const integrations = [
  { name: "MTN MoMo", category: "Mobile Money", color: "#FFCC00" },
  { name: "Airtel Money", category: "Mobile Money", color: "#E40000" },
  { name: "Visa", category: "Cards", color: "#1A1F71" },
  { name: "Mastercard", category: "Cards", color: "#EB001B" },
  { name: "Stanbic", category: "Banks", color: "#0033A0" },
  { name: "Centenary", category: "Banks", color: "#FFD700" },
  { name: "Google", category: "Cloud", color: "#4285F4" },
  { name: "Microsoft", category: "Cloud", color: "#00A4EF" },
  { name: "WhatsApp", category: "Messaging", color: "#25D366" },
  { name: "SMS Gateway", category: "Messaging", color: "#34B7F1" },
  { name: "Canvas LMS", category: "Learning", color: "#E13F2A" },
  { name: "Moodle", category: "Learning", color: "#F98012" },
  { name: "Zoom", category: "Communication", color: "#2D8CFF" },
  { name: "Google Maps", category: "Maps", color: "#34A853" },
  { name: "Biometric", category: "Hardware", color: "#7B68EE" },
  { name: "POS Terminals", category: "Hardware", color: "#7B1416" },
  { name: "QR / NFC", category: "Hardware", color: "#C4A882" },
  { name: "GPS", category: "Hardware", color: "#6B5535" },
  { name: "Government API", category: "Compliance", color: CHARCOAL },
  { name: "NIRA", category: "Compliance", color: WINE },
];

export function IntegrationsSection() {
  return (
    <div>
      <div className="mb-12 max-w-2xl">
        <div className="mb-6 flex items-center gap-4">
          <div className="h-px w-12" style={{ background: WINE }} />
          <span className="text-[11px] font-medium uppercase tracking-[0.28em]" style={{ color: WINE }}>
            Integrations
          </span>
        </div>
        <h2 className="font-display text-4xl font-medium leading-[1.1] tracking-tight sm:text-5xl lg:text-[3.5rem] text-balance" style={{ color: CHARCOAL }}>
          Connected to everything that matters.
        </h2>
        <p className="mt-6 text-lg leading-[1.7] text-[#4A4A4A]">
          Mobile money networks, banks, card processors, learning platforms, messaging
          channels, hardware, and government APIs — TriSphere integrates with every
          system an institution needs.
        </p>
      </div>

      {/* Integration grid */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-5">
        {integrations.map((int, i) => (
          <motion.div
            key={int.name}
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.04, type: "spring", stiffness: 100 }}
            whileHover={{ y: -4 }}
            className="group flex flex-col items-center gap-3 rounded-2xl border bg-white p-5 text-center transition-shadow hover:shadow-premium"
            style={{ borderColor: "rgba(26,26,26,0.06)" }}
          >
            <div
              className="grid h-12 w-12 place-items-center rounded-xl text-sm font-bold"
              style={{ background: `${int.color}15`, color: int.color }}
            >
              {int.name[0]}
            </div>
            <div>
              <p className="text-sm font-medium" style={{ color: CHARCOAL }}>{int.name}</p>
              <p className="mt-0.5 text-[10px] uppercase tracking-wider text-[#999]">{int.category}</p>
            </div>
            <div
              className="h-1 w-8 rounded-full opacity-0 transition-opacity group-hover:opacity-100"
              style={{ background: int.color }}
            />
          </motion.div>
        ))}
      </div>

      {/* Open API banner */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="mt-8 flex flex-col items-center justify-between gap-4 rounded-2xl border p-8 sm:flex-row" style={{ background: CHARCOAL, borderColor: "rgba(255,255,255,0.06)" }}
      >
        <div>
          <h3 className="font-display text-2xl font-medium text-white">Open REST & GraphQL APIs</h3>
          <p className="mt-2 text-sm text-white/70">
            Build custom integrations with full documentation, sandbox environments, and webhooks.
          </p>
        </div>
        <button
          className="flex shrink-0 items-center gap-2 rounded-full px-6 py-3 text-sm font-medium text-white transition-all hover:shadow-lg"
          style={{ background: WINE }}
        >
          Explore the API
          <ArrowRightIcon className="h-4 w-4" accent="#fff" style={{ color: "#fff" }} />
        </button>
      </motion.div>
    </div>
  );
}

// ============================================================
// LIVE NUMBERS — animated counters
// ============================================================
const liveStats = [
  { value: 126, label: "Schools", suffix: "", color: CHARCOAL },
  { value: 7492, label: "Students", suffix: "", color: WINE },
  { value: 1284, label: "Teachers", suffix: "", color: CHARCOAL },
  { value: 19842, label: "Parents", suffix: "", color: WINE },
  { value: 1000000, label: "Payments processed", suffix: "+", color: CHARCOAL },
  { value: 96, label: "Buses tracked", suffix: "", color: WINE },
  { value: 3, label: "Countries", suffix: "", color: CHARCOAL },
  { value: 12, label: "Cities", suffix: "", color: WINE },
];

export function LiveNumbersSection() {
  return (
    <div className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-black/[0.06] bg-black/[0.06] sm:grid-cols-4">
      {liveStats.map((stat, i) => (
        <motion.div
          key={stat.label}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: i * 0.06, duration: 0.5 }}
          className="flex flex-col items-center justify-center px-4 py-10 text-center" style={{ background: CREAM }}
        >
          <AnimatedCounter value={stat.value} suffix={stat.suffix} color={stat.color} />
          <p className="mt-3 text-sm text-[#888]">{stat.label}</p>
        </motion.div>
      ))}
    </div>
  );
}

function AnimatedCounter({ value, suffix, color }: { value: number; suffix: string; color: string }) {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const duration = 2000;
    const start = Date.now();
    const id = setInterval(() => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.floor(value * eased));
      if (progress >= 1) clearInterval(id);
    }, 16);
    return () => clearInterval(id);
  }, [value]);

  const formatted = display >= 1000000
    ? `${(display / 1000000).toFixed(1)}M`
    : display >= 1000
    ? `${(display / 1000).toFixed(1)}K`
    : display.toLocaleString();

  return (
    <motion.div
      initial={{ scale: 0.8 }}
      whileInView={{ scale: 1 }}
      viewport={{ once: true }}
      transition={{ type: "spring", stiffness: 100 }}
      className="font-display text-4xl font-medium tabular-nums sm:text-5xl"
      style={{ color }}
    >
      {formatted}{suffix}
    </motion.div>
  );
}

// ============================================================
// IMPLEMENTATION TIMELINE
// ============================================================
const timelineSteps = [
  { week: "Week 1", title: "Discovery", desc: "We map your institution's workflows, fee structures, and systems.", color: CHARCOAL },
  { week: "Week 2", title: "Migration", desc: "Students, staff, and historical data imported. Zero downtime.", color: WINE },
  { week: "Week 3", title: "Training", desc: "Admins, teachers, and parents onboarded. Live support throughout.", color: CHARCOAL },
  { week: "Week 4", title: "Go Live", desc: "First payments processed. First buses tracked. First reports generated.", color: WINE },
  { week: "Week 5", title: "Optimization", desc: "We tune the platform based on real usage data and feedback.", color: CHARCOAL },
];

export function ImplementationTimeline() {
  return (
    <div className="relative">
      {/* Connecting line */}
      <div className="absolute left-0 right-0 top-12 hidden h-px bg-gradient-to-r from-transparent via-black/10 to-transparent lg:block" />

      <div className="grid gap-8 lg:grid-cols-5">
        {timelineSteps.map((step, i) => (
          <motion.div
            key={step.week}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.12, duration: 0.5 }}
            className="relative flex flex-col items-center text-center"
          >
            {/* Node */}
            <div
              className="relative z-10 grid h-24 w-24 place-items-center rounded-full border-4"
              style={{ background: CREAM, borderColor: step.color }}
            >
              <span className="font-display text-2xl font-medium tabular-nums" style={{ color: step.color }}>
                {i + 1}
              </span>
            </div>

            {/* Content */}
            <div className="mt-5">
              <p className="text-[10px] font-medium uppercase tracking-[0.2em]" style={{ color: step.color }}>
                {step.week}
              </p>
              <h3 className="mt-2 font-display text-xl font-medium" style={{ color: CHARCOAL }}>{step.title}</h3>
              <p className="mt-2 text-xs leading-relaxed text-[#888]">{step.desc}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
